import {
  SandpackCodeEditor,
  SandpackConsole,
  SandpackPreview,
  SandpackProvider,
  type SandpackTheme,
} from '@codesandbox/sandpack-react';
import { useScreenSize, type ScreenSize } from '#app/hooks/use-screen-size';
import {
  type ProjectSchemaModel,
  type FullProjectSchemaModel,
} from '#app/utils/sandpack';
import { DuneEmbedWrapper } from './dune-embed-wrapper';

type DuneEmbedProps = ProjectSchemaModel & {
  paid?: FullProjectSchemaModel['paid'];
  theme: SandpackTheme;
};

type DisplayComponents = DuneEmbedProps['display']['components'];

const displayComponents: Record<
  DisplayComponents[string]['type'],
  React.FC<any>
> = {
  editor: SandpackCodeEditor,
  preview: SandpackPreview,
  console: SandpackConsole,
};

const isLayoutValid = (layout = '') => {
  const filteredLayout = layout
    .replace(/Empty/g, '')
    .replace(/'/g, '')
    .replace(/\n/, '')
    .trim();

  return filteredLayout !== '';
};

const getGridTemplateAreas = (
  layout: DuneEmbedProps['display']['layouts'],
  screenSize: ScreenSize,
) => {
  if (screenSize === 'large') {
    if (isLayoutValid(layout.large)) return layout.large;
    if (isLayoutValid(layout.medium)) return layout.medium;
  }

  if (screenSize === 'medium') {
    if (isLayoutValid(layout.medium)) return layout.medium;
  }

  return isLayoutValid(layout.small) ? layout.small : '';
};

export const DuneEmbed: React.FC<DuneEmbedProps> = (props) => {
  const screenSize = useScreenSize();

  const gridTemplateAreas = getGridTemplateAreas(
    props.display.layouts,
    screenSize,
  )?.replace(/Empty/g, '.');

  if (!gridTemplateAreas) {
    return <>Please fill out a small layout.</>;
  }

  const { theme, ...generalOptions } = props.display.general;
  const templateRows = gridTemplateAreas.split('\n');
  const templateColumns = templateRows[0]?.split(' ') ?? [];

  const includesPreviewComponent = Object.entries(
    props.display.components,
  ).some(([key, { type }]) => {
    // The Preview component must be in the actual layout
    return gridTemplateAreas.includes(key) && type === 'preview';
  });

  return (
    <SandpackProvider
      style={{ height: '100%', flex: 1 }}
      options={generalOptions}
      theme={props.theme}
      {...props.core}
    >
      <DuneEmbedWrapper {...props}>
        {(config) => (
          <div
            style={{
              height: '100%',
              display: 'grid',
              gridTemplateAreas,
              gridTemplateRows: templateRows.fill('1fr').join(' '),
              gridTemplateColumns: templateColumns.fill('1fr').join(' '),
            }}
          >
            {Object.entries(props.display.components).map(
              ([key, { type, data }]) => {
                if (!gridTemplateAreas.includes(key)) return null;

                const Component = displayComponents[type];
                if (!Component) return null;

                const props = {
                  style: { gridArea: key, overflow: 'auto' },
                  ...data,
                };

                if (type === 'editor') {
                  return (
                    <Component key={key} ref={config.editorRef} {...props} />
                  );
                }

                if (type === 'preview') {
                  return <Component key={key} {...props} />;
                }

                if (type === 'console') {
                  return (
                    <Component
                      key={key}
                      {...props}
                      // Automatically enable standalone mode
                      // if a Preview component doesn't exist:
                      // https://sandpack.codesandbox.io/docs/advanced-usage/components#usage-1
                      standalone={!includesPreviewComponent}
                    />
                  );
                }

                return <Component key={key} {...props} />;
              },
            )}
          </div>
        )}
      </DuneEmbedWrapper>
    </SandpackProvider>
  );
};
